'use client';

import { c as _c } from "react/compiler-runtime";
import { isFundingHostPicker, isHostedFundingMethod, onrampManifest } from '@morfi/onramp-sdk';
import type { useFundingAmountDraft, useFundingInlineCheckout } from '@morfi/onramp-sdk/react';
import type { MoonPayWebProvider } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { BankTransferInstructions } from '../bank-transfers/bank-transfer-instructions';
import { FundingReceipt } from '../history/funding-receipt';
import { FundingProfile } from '../profile/funding-profile';
import { FundingLoading } from '../shared/funding-loading';
import { FundingProviderLoading } from '../shared/funding-provider-loading';
import { FundingCustomerVerificationForm } from '../verification/funding-customer-verification';
import { FundingVerification } from '../verification/funding-verification';
import { FundingAuthenticationTerms } from './funding-authentication-terms';
import { FundingCardEntry } from './funding-card-entry';
import type { Props } from './funding-checkout';
import { FundingCheckoutAmount } from './funding-checkout-amount';
import { FundingCheckoutFeedback } from './funding-checkout-feedback';
import { FundingConnectionRequired } from './funding-connection-required';
import { FundingMethodPicker } from './funding-method-picker';
import { FundingPaymentReview } from './funding-payment-review';
import { FundingProviderFrame } from './funding-provider-frame';
import { FundingHostedWidget } from './hosted/funding-hosted-widget';
export function FundingCheckoutView(t0) {
  const $ = _c(154);
  const {
    checkout,
    amountDraft,
    client,
    realtime,
    sessionProofId,
    authenticationRequested,
    authenticationBusy,
    authenticationError,
    onAcceptAuthenticationTerms,
    provider,
    onSessionRefresh,
    onSignIn,
    onExchange,
    currency,
    onClose,
    directPurchase,
    decimals
  } = t0;
  const t = useTranslations("onramp");
  const lowKycEligible = !directPurchase && onrampManifest.settings.guest_checkout.enabled && checkout.profileConnection?.guest === true;
  if (checkout.state === "profile") {
    const t1 = checkout.loading || authenticationBusy;
    const t2 = checkout.review?.estimate.currency ?? checkout.preview.review?.estimate.currency ?? currency;
    const t3 = checkout.review?.estimate.decimals ?? checkout.preview.review?.estimate.decimals ?? decimals;
    const t4 = Boolean(checkout.error) && checkout.error !== "disconnect_failed";
    let t5;
    if ($[0] !== checkout.back || $[1] !== checkout.busy || $[2] !== checkout.canDisconnect || $[3] !== checkout.disconnect || $[4] !== checkout.profileConnection || $[5] !== checkout.profileUpdated || $[6] !== checkout.profileVerify || $[7] !== client || $[8] !== onSessionRefresh || $[9] !== provider || $[10] !== sessionProofId || $[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
      t5 = <FundingProfile canDisconnect={checkout.canDisconnect} disconnectBusy={checkout.busy} onDisconnect={checkout.disconnect} connection={checkout.profileConnection} connectionPending={t1} sessionProofId={sessionProofId} client={client} provider={provider} currency={t2} decimals={t3} onBack={checkout.back} onUpdated={checkout.profileUpdated} onRefreshEligibility={onSessionRefresh} onVerify={checkout.profileVerify} verificationError={t4} />;
      $[0] = checkout.back;
      $[1] = checkout.busy;
      $[2] = checkout.canDisconnect;
      $[3] = checkout.disconnect;
      $[4] = checkout.profileConnection;
      $[5] = checkout.profileUpdated;
      $[6] = checkout.profileVerify;
      $[7] = client;
      $[8] = onSessionRefresh;
      $[9] = provider;
      $[10] = sessionProofId;
      $[11] = t1;
      $[12] = t2;
      $[13] = t3;
      $[14] = t4;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return t5;
  }
  if (checkout.loading && checkout.state === "methods") {
    const t1 = Boolean(directPurchase);
    let t2;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = [];
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    let t3;
    if ($[17] !== client || $[18] !== onExchange || $[19] !== t1) {
      t3 = <FundingMethodPicker directPurchase={t1} client={client} methods={t2} onExchange={onExchange} loading={true} />;
      $[17] = client;
      $[18] = onExchange;
      $[19] = t1;
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    return t3;
  }
  if (checkout.method && isHostedFundingMethod(checkout.method) && checkout.state === "amount" && checkout.busy && !checkout.attemptId) {
    let t1;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FundingProviderLoading />;
      $[21] = t1;
    } else {
      t1 = $[21];
    }
    return t1;
  }
  if (checkout.loading && !checkout.paymentSignature) {
    let t1;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FundingLoading />;
      $[22] = t1;
    } else {
      t1 = $[22];
    }
    return t1;
  }
  if (isFundingHostPicker(checkout, Boolean(directPurchase), authenticationRequested)) {
    let t1;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = [];
      $[23] = t1;
    } else {
      t1 = $[23];
    }
    let t2;
    if ($[24] !== client || $[25] !== onExchange) {
      t2 = <FundingMethodPicker client={client} methods={t1} onExchange={onExchange} />;
      $[24] = client;
      $[25] = onExchange;
      $[26] = t2;
    } else {
      t2 = $[26];
    }
    return t2;
  }
  if (checkout.state === "terms") {
    let t1;
    if ($[27] !== authenticationBusy || $[28] !== authenticationError || $[29] !== client || $[30] !== onAcceptAuthenticationTerms) {
      t1 = <FundingAuthenticationTerms client={client} busy={authenticationBusy} error={authenticationError} onAccept={onAcceptAuthenticationTerms} />;
      $[27] = authenticationBusy;
      $[28] = authenticationError;
      $[29] = client;
      $[30] = onAcceptAuthenticationTerms;
      $[31] = t1;
    } else {
      t1 = $[31];
    }
    return t1;
  }
  if (checkout.state === "verification") {
    const t1 = checkout.location?.country ?? undefined;
    let t2;
    if ($[32] !== authenticationError || $[33] !== checkout.back || $[34] !== client || $[35] !== onSessionRefresh || $[36] !== t1) {
      t2 = <FundingVerification client={client} sessionError={authenticationError} onContinue={onSessionRefresh} onBack={checkout.back} country={t1} />;
      $[32] = authenticationError;
      $[33] = checkout.back;
      $[34] = client;
      $[35] = onSessionRefresh;
      $[36] = t1;
      $[37] = t2;
    } else {
      t2 = $[37];
    }
    return t2;
  }
  if (checkout.state === "customer_verification" && checkout.customerVerification) {
    let t1;
    if ($[38] !== checkout.customerVerification || $[39] !== checkout.retry || $[40] !== checkout.verificationFromProfile || $[41] !== client || $[42] !== provider) {
      t1 = <FundingCustomerVerificationForm client={client} key={checkout.customerVerification.customerId} provider={provider} verification={checkout.customerVerification} onComplete={checkout.retry} fromProfile={checkout.verificationFromProfile} />;
      $[38] = checkout.customerVerification;
      $[39] = checkout.retry;
      $[40] = checkout.verificationFromProfile;
      $[41] = client;
      $[42] = provider;
      $[43] = t1;
    } else {
      t1 = $[43];
    }
    return t1;
  }
  if (checkout.state === "status" && checkout.pendingId) {
    let t1;
    if ($[44] !== checkout) {
      t1 = checkout.error ? <FundingCheckoutFeedback checkout={checkout} /> : null;
      $[44] = checkout;
      $[45] = t1;
    } else {
      t1 = $[45];
    }
    let t2;
    if ($[46] !== checkout.operation || $[47] !== checkout.pendingId) {
      t2 = checkout.operation?.id === checkout.pendingId ? {
        initialOperation: checkout.operation
      } : {};
      $[46] = checkout.operation;
      $[47] = checkout.pendingId;
      $[48] = t2;
    } else {
      t2 = $[48];
    }
    let t3;
    if ($[49] !== checkout.operation?.id || $[50] !== checkout.pendingId || $[51] !== checkout.review) {
      t3 = checkout.review && checkout.operation?.id === checkout.pendingId ? {
        quoted: {
          operationId: checkout.pendingId,
          review: checkout.review
        }
      } : {};
      $[49] = checkout.operation?.id;
      $[50] = checkout.pendingId;
      $[51] = checkout.review;
      $[52] = t3;
    } else {
      t3 = $[52];
    }
    let t4;
    if ($[53] !== realtime) {
      t4 = realtime ? {
        realtime
      } : {};
      $[53] = realtime;
      $[54] = t4;
    } else {
      t4 = $[54];
    }
    let t5;
    if ($[55] !== checkout.back || $[56] !== checkout.pendingId || $[57] !== client || $[58] !== onClose || $[59] !== onSignIn || $[60] !== t2 || $[61] !== t3 || $[62] !== t4) {
      t5 = <FundingReceipt onReturnToPurchase={onClose} progress={true} {...t2} {...t3} client={client} {...t4} operationId={checkout.pendingId} onBack={checkout.back} onSignIn={onSignIn} />;
      $[55] = checkout.back;
      $[56] = checkout.pendingId;
      $[57] = client;
      $[58] = onClose;
      $[59] = onSignIn;
      $[60] = t2;
      $[61] = t3;
      $[62] = t4;
      $[63] = t5;
    } else {
      t5 = $[63];
    }
    let t6;
    if ($[64] !== checkout || $[65] !== t) {
      t6 = checkout.canResumeChallenge ? <AnimatedButton variant="secondary" loading={checkout.busy} disabled={checkout.busy} onClick={() => {
        checkout.resumeChallenge();
      }}>{t("resume_verification")}</AnimatedButton> : null;
      $[64] = checkout;
      $[65] = t;
      $[66] = t6;
    } else {
      t6 = $[66];
    }
    let t7;
    if ($[67] !== t1 || $[68] !== t5 || $[69] !== t6) {
      t7 = <>{t1}{t5}{t6}</>;
      $[67] = t1;
      $[68] = t5;
      $[69] = t6;
      $[70] = t7;
    } else {
      t7 = $[70];
    }
    return t7;
  }
  if (checkout.state === "connection_required" || checkout.state === "unavailable" || checkout.state === "methods" && !checkout.error && (checkout.methods.length === 0 || checkout.authenticationAvailable)) {
    const t1 = Boolean(directPurchase);
    let t2;
    if ($[71] !== checkout) {
      t2 = id => {
        checkout.deleteSavedCard(id);
      };
      $[71] = checkout;
      $[72] = t2;
    } else {
      t2 = $[72];
    }
    const t3 = checkout.state !== "unavailable" && checkout.authenticationAvailable;
    let t4;
    if ($[73] !== authenticationBusy || $[74] !== authenticationError || $[75] !== checkout.authenticationReason || $[76] !== checkout.location || $[77] !== checkout.retry || $[78] !== t3) {
      t4 = <FundingConnectionRequired reason={checkout.authenticationReason} location={checkout.location} onRefresh={checkout.retry} canAuthenticate={t3} busy={authenticationBusy} error={authenticationError} />;
      $[73] = authenticationBusy;
      $[74] = authenticationError;
      $[75] = checkout.authenticationReason;
      $[76] = checkout.location;
      $[77] = checkout.retry;
      $[78] = t3;
      $[79] = t4;
    } else {
      t4 = $[79];
    }
    let t5;
    if ($[80] !== checkout.busy || $[81] !== checkout.methods || $[82] !== checkout.openProfile || $[83] !== checkout.savedCards || $[84] !== checkout.select || $[85] !== checkout.selectSavedCard || $[86] !== checkout.unavailableBankMethods || $[87] !== checkout.unavailableMethods || $[88] !== client || $[89] !== lowKycEligible || $[90] !== onExchange || $[91] !== t1 || $[92] !== t2 || $[93] !== t4) {
      t5 = <FundingMethodPicker directPurchase={t1} client={client} methods={checkout.methods} unavailableBankMethods={checkout.unavailableBankMethods} unavailableMethods={checkout.unavailableMethods} lowKycEligible={lowKycEligible} onSelect={checkout.select} onExchange={onExchange} onProfile={checkout.openProfile} feedbackAfterMethods={true} savedCards={checkout.savedCards} busy={checkout.busy} onSelectCard={checkout.selectSavedCard} onRemoveCard={t2} providerFeedback={t4} />;
      $[80] = checkout.busy;
      $[81] = checkout.methods;
      $[82] = checkout.openProfile;
      $[83] = checkout.savedCards;
      $[84] = checkout.select;
      $[85] = checkout.selectSavedCard;
      $[86] = checkout.unavailableBankMethods;
      $[87] = checkout.unavailableMethods;
      $[88] = client;
      $[89] = lowKycEligible;
      $[90] = onExchange;
      $[91] = t1;
      $[92] = t2;
      $[93] = t4;
      $[94] = t5;
    } else {
      t5 = $[94];
    }
    return t5;
  }
  if (checkout.state === "connect" && !checkout.paymentSignature) {
    let t1;
    if ($[95] !== checkout || $[96] !== provider) {
      t1 = <FundingProviderFrame key={checkout.state} provider={provider} checkout={checkout} />;
      $[95] = checkout;
      $[96] = provider;
      $[97] = t1;
    } else {
      t1 = $[97];
    }
    return t1;
  }
  if (checkout.state === "bank_transfer_instructions" && checkout.pendingId) {
    let t1;
    if ($[98] !== checkout.pendingId || $[99] !== checkout.viewPaymentStatus || $[100] !== client || $[101] !== onClose) {
      t1 = <BankTransferInstructions client={client} operationId={checkout.pendingId} onDismiss={onClose} onViewStatus={checkout.viewPaymentStatus} />;
      $[98] = checkout.pendingId;
      $[99] = checkout.viewPaymentStatus;
      $[100] = client;
      $[101] = onClose;
      $[102] = t1;
    } else {
      t1 = $[102];
    }
    return t1;
  }
  if (checkout.method && isHostedFundingMethod(checkout.method) && (checkout.state === "amount" || checkout.state === "payment")) {
    let t1;
    if ($[103] !== amountDraft || $[104] !== checkout || $[105] !== provider) {
      t1 = <FundingCheckoutAmount checkout={checkout} amountDraft={amountDraft} provider={provider} />;
      $[103] = amountDraft;
      $[104] = checkout;
      $[105] = provider;
      $[106] = t1;
    } else {
      t1 = $[106];
    }
    const t2 = checkout.state !== "payment";
    const t3 = checkout.state !== "payment";
    let t4;
    if ($[107] !== checkout || $[108] !== client || $[109] !== provider) {
      t4 = checkout.paymentSignature ? <FundingHostedWidget key={checkout.attemptId} client={client} provider={provider} checkout={checkout} /> : null;
      $[107] = checkout;
      $[108] = client;
      $[109] = provider;
      $[110] = t4;
    } else {
      t4 = $[110];
    }
    let t5;
    if ($[111] !== t2 || $[112] !== t3 || $[113] !== t4) {
      t5 = <div hidden={t2} inert={t3}>{t4}</div>;
      $[111] = t2;
      $[112] = t3;
      $[113] = t4;
      $[114] = t5;
    } else {
      t5 = $[114];
    }
    let t6;
    if ($[115] !== t1 || $[116] !== t5) {
      t6 = <div className="flex flex-col gap-4">{t1}{t5}</div>;
      $[115] = t1;
      $[116] = t5;
      $[117] = t6;
    } else {
      t6 = $[117];
    }
    return t6;
  }
  if (checkout.state === "card_entry") {
    let t1;
    if ($[118] !== checkout) {
      t1 = <FundingCheckoutFeedback checkout={checkout} />;
      $[118] = checkout;
      $[119] = t1;
    } else {
      t1 = $[119];
    }
    let t2;
    if ($[120] !== checkout || $[121] !== provider) {
      t2 = <FundingCardEntry provider={provider} checkout={checkout} />;
      $[120] = checkout;
      $[121] = provider;
      $[122] = t2;
    } else {
      t2 = $[122];
    }
    let t3;
    if ($[123] !== t1 || $[124] !== t2) {
      t3 = <>{t1}{t2}</>;
      $[123] = t1;
      $[124] = t2;
      $[125] = t3;
    } else {
      t3 = $[125];
    }
    return t3;
  }
  let t1;
  if ($[126] !== checkout || $[127] !== provider) {
    t1 = <FundingPaymentReview provider={provider} checkout={checkout} />;
    $[126] = checkout;
    $[127] = provider;
    $[128] = t1;
  } else {
    t1 = $[128];
  }
  let t2;
  if ($[129] !== checkout) {
    t2 = !checkout.amountEntry && !(checkout.method === "sepa" && checkout.state === "review") ? <FundingCheckoutFeedback checkout={checkout} /> : null;
    $[129] = checkout;
    $[130] = t2;
  } else {
    t2 = $[130];
  }
  let t3;
  if ($[131] !== amountDraft || $[132] !== checkout || $[133] !== provider) {
    t3 = <FundingCheckoutAmount checkout={checkout} amountDraft={amountDraft} provider={provider} />;
    $[131] = amountDraft;
    $[132] = checkout;
    $[133] = provider;
    $[134] = t3;
  } else {
    t3 = $[134];
  }
  let t4;
  if ($[135] !== checkout || $[136] !== provider) {
    t4 = checkout.state === "connect" ? <FundingProviderFrame key="connect" provider={provider} checkout={checkout} /> : null;
    $[135] = checkout;
    $[136] = provider;
    $[137] = t4;
  } else {
    t4 = $[137];
  }
  let t5;
  if ($[138] !== checkout || $[139] !== provider) {
    t5 = checkout.state === "challenge" ? <FundingProviderFrame key="challenge" provider={provider} checkout={checkout} /> : null;
    $[138] = checkout;
    $[139] = provider;
    $[140] = t5;
  } else {
    t5 = $[140];
  }
  let t6;
  if ($[141] !== checkout || $[142] !== client || $[143] !== directPurchase || $[144] !== lowKycEligible || $[145] !== onExchange) {
    t6 = checkout.state === "methods" ? <FundingMethodPicker directPurchase={Boolean(directPurchase)} client={client} methods={checkout.methods} unavailableBankMethods={checkout.unavailableBankMethods} unavailableMethods={checkout.unavailableMethods} lowKycEligible={lowKycEligible} onSelect={checkout.select} onExchange={onExchange} onProfile={checkout.openProfile} savedCards={checkout.savedCards} busy={checkout.busy} onSelectCard={checkout.selectSavedCard} onRemoveCard={id_0 => {
      checkout.deleteSavedCard(id_0);
    }} /> : null;
    $[141] = checkout;
    $[142] = client;
    $[143] = directPurchase;
    $[144] = lowKycEligible;
    $[145] = onExchange;
    $[146] = t6;
  } else {
    t6 = $[146];
  }
  let t7;
  if ($[147] !== t1 || $[148] !== t2 || $[149] !== t3 || $[150] !== t4 || $[151] !== t5 || $[152] !== t6) {
    t7 = <div className="flex flex-col gap-4">{t1}{t2}{t3}{t4}{t5}{t6}</div>;
    $[147] = t1;
    $[148] = t2;
    $[149] = t3;
    $[150] = t4;
    $[151] = t5;
    $[152] = t6;
    $[153] = t7;
  } else {
    t7 = $[153];
  }
  return t7;
}